import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  View
} from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { StatusBar } from 'expo-status-bar'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { observer } from 'mobx-react-lite'
import { useQuery, useMutation } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { api } from '@ppwr/convex/_generated/api'
import { DS } from '@/design-system'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { LoungeAvatar } from '../../components/LoungeAvatar'
import { LoungeFullModal } from './LoungeFullModal'
import { isConvexErrorWithCode } from '../../utils/errors/convex'
import { translate } from '../../i18n/translate'
import { LOUNGE_FULL_ERROR_CODE } from '@ppwr/convex/lounges/capacity'
import {
  INVALID_JOIN_CODE_ERROR_CODE,
  LOUNGE_INACTIVE_ERROR_CODE,
  LOUNGE_REJOIN_REQUIRES_OWNER_ERROR_CODE
} from '@ppwr/convex/lounges/errors'

function errorMessageIncludes(err: unknown, fragment: string) {
  return err instanceof Error && err.message.toLowerCase().includes(fragment)
}

/**
 * Maps a `joinLoungeByCode` failure to user-facing copy. Never surfaces the raw
 * Convex message; the substring checks cover backends deployed before the error codes existed.
 */
function getJoinErrorMessage(err: unknown): string {
  if (isConvexErrorWithCode(err, INVALID_JOIN_CODE_ERROR_CODE) || errorMessageIncludes(err, 'invalid share code')) {
    return translate('loungeJoinScreen.invalidCode')
  }
  if (
    isConvexErrorWithCode(err, LOUNGE_REJOIN_REQUIRES_OWNER_ERROR_CODE) ||
    errorMessageIncludes(err, 'contact the lounge owner to rejoin')
  ) {
    return translate('loungeJoinScreen.rejoinRequiresOwner')
  }
  if (isConvexErrorWithCode(err, LOUNGE_INACTIVE_ERROR_CODE) || errorMessageIncludes(err, 'not currently active')) {
    return translate('loungeJoinScreen.loungeInactive')
  }
  return translate('loungeJoinScreen.genericError')
}

// eslint-disable-next-line @typescript-eslint/no-var-requires
const backIcon = require('../../../assets/mainHeader/back.png')

function isLoungeFullError(err: unknown) {
  return isConvexErrorWithCode(err, LOUNGE_FULL_ERROR_CODE) || errorMessageIncludes(err, 'lounge is full')
}

export const LoungeJoinScreen = observer(function LoungeJoinScreen() {
  const router = useRouter()
  const { code: codeParam } = useLocalSearchParams<{ code?: string }>()
  const {
    analytics: { setEvent },
    session: { user, isTeacher, isAdmin, isUserInfoLoaded, userInfo }
  } = useStore()
  const userId = user?.auth0Id ?? ''
  const mongoUserId = user?._id?.toString()
  const userName = user?.name ?? 'Player'
  const hasFinishedOnboarding = isUserInfoLoaded && userInfo?.isUserNew === true

  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const [code, setCode] = useState(codeParam?.toUpperCase() ?? '')
  const [error, setError] = useState<string | null>(null)
  const [joining, setJoining] = useState(false)
  const [loungeFullModalVisible, setLoungeFullModalVisible] = useState(false)

  const preview = useQuery(
    api.lounges.queries.getLoungeByJoinCode,
    code.length >= 8 ? { joinCode: code.toUpperCase() } : 'skip'
  )

  const joinMutation = useMutation(api.lounges.mutations.joinLoungeByCode)

  const openLoungeFullModal = useCallback(() => {
    setLoungeFullModalVisible(true)
    setError(null)
  }, [])

  /** Resolves to a failure reason, or `undefined` once the user has been sent into the lounge. */
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: join + Convex error branches
  const handleJoin = useCallback(
    async ({ fromLink = false } = {}): Promise<string | undefined> => {
      if (!code.trim()) return 'empty_code'
      if (!userId || !mongoUserId) return 'missing_user'
      setError(null)

      if (preview?.isFull) {
        openLoungeFullModal()
        return 'lounge_full'
      }

      setJoining(true)
      try {
        const result = await joinMutation({
          joinCode: code.toUpperCase(),
          userId,
          userName,
          mongoUserId
        })
        setEvent(events.lounge.join_lounge_submitted, {
          userId: userId,
          loungeId: result.loungeId ?? undefined,
          joinCode: code.toUpperCase(),
          isTeacher,
          isAdmin,
          fromLink,
          alreadyMember: result.alreadyMember
        })
        // The user may have backed out while the join was in flight.
        if (isMountedRef.current) {
          router.replace({ pathname: '/lounges/[loungeId]', params: { loungeId: result.loungeId } } as any)
        }
        return undefined
      } catch (err: unknown) {
        if (isLoungeFullError(err)) {
          openLoungeFullModal()
          return 'lounge_full'
        }
        setError(getJoinErrorMessage(err))
        return 'join_error'
      } finally {
        setJoining(false)
      }
    },
    [
      code,
      userId,
      mongoUserId,
      userName,
      isTeacher,
      isAdmin,
      joinMutation,
      router,
      setEvent,
      preview?.isFull,
      openLoungeFullModal
    ]
  )

  const { isAutoJoining, hasTimedOut, retry } = useAutoJoinFromLink({
    enabled: !!codeParam,
    isUserReady: !!user && hasFinishedOnboarding,
    userId,
    mongoUserId,
    code,
    preview,
    handleJoin,
    setEvent
  })

  if (isAutoJoining) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar style="dark" />
        <JoinHeader />
        {hasTimedOut ? (
          <AutoJoinTimeoutView
            onRetry={retry}
            onExit={() => (router.canGoBack() ? router.back() : router.replace('/lounges' as any))}
          />
        ) : (
          <AutoJoiningView loungeName={preview?.name} />
        )}
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar style="dark" />
      <JoinHeader />

      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.body}>
        <DS.Frame gap="16">
          <DS.Text variant="Body Large/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
            Enter the share code you received from the lounge owner.
          </DS.Text>

          <TextInput
            style={styles.input}
            placeholder="Share code (e.g. AB3XY7K2PQ)"
            placeholderTextColor={DS.palette['Brand/Dark Grey']}
            value={code}
            onChangeText={(t) => {
              setCode(t.toUpperCase())
              setError(null)
            }}
            autoCapitalize="characters"
            autoCorrect={false}
            maxLength={10}
          />

          {code.length >= 8 && preview !== undefined && <LoungePreviewCard preview={preview} />}

          {error && (
            <DS.Text variant="Body Large/Regular" style={{ color: DS.palette['Utility/Error'] }}>
              {error}
            </DS.Text>
          )}

          <DS.Button
            type="On Light"
            variant="Large/Primary"
            backgroundColor="Brand/Maroon"
            onPress={() => handleJoin()}
            disabled={joining || code.length < 8 || preview === null}
          >
            {joining
              ? translate('loungeJoinScreen.joining')
              : preview?.isFull
                ? translate('loungeJoinScreen.loungeFull')
                : translate('loungeJoinScreen.joinLounge')}
          </DS.Button>
        </DS.Frame>
      </KeyboardAvoidingView>

      <LoungeFullModal visible={loungeFullModalVisible} onDismiss={() => setLoungeFullModalVisible(false)} />
    </SafeAreaView>
  )
})

type AutoJoinParams = {
  enabled: boolean
  /** User loaded and onboarding finished. Before that, the onboarding redirect replaces this screen. */
  isUserReady: boolean
  userId: string
  mongoUserId?: string
  code: string
  preview: { name: string; status: string } | null | undefined
  handleJoin: (options: { fromLink: boolean }) => Promise<string | undefined>
  setEvent: (event: string, payload?: Record<string, string>) => void
}

const AUTO_JOIN_TIMEOUT_MS = 60_000

/**
 * A join link carries the code, so join as soon as it has been looked up instead of
 * asking for it. `isAutoJoining` turns `false` once the attempt fails, so the screen falls back
 * to the form. If the user, the lookup or the join hasn't come back within a minute,
 * `hasTimedOut` turns `true` until `retry` is called.
 */
function useAutoJoinFromLink({
  enabled,
  isUserReady,
  userId,
  mongoUserId,
  code,
  preview,
  handleJoin,
  setEvent
}: AutoJoinParams) {
  const [isAutoJoining, setIsAutoJoining] = useState(enabled)
  const [hasTimedOut, setHasTimedOut] = useState(false)
  const attemptedRef = useRef(false)
  const isJoinInFlightRef = useRef(false)

  useEffect(() => {
    if (!isAutoJoining || hasTimedOut) return
    const timer = setTimeout(() => {
      setEvent(events.lounge.join_lounge_link_failed, { userId, joinCode: code, reason: 'timeout' })
      setHasTimedOut(true)
    }, AUTO_JOIN_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [isAutoJoining, hasTimedOut, userId, code, setEvent])

  const retry = useCallback(() => {
    // A join still in flight may yet land; restarting it would send a second request.
    if (!isJoinInFlightRef.current) attemptedRef.current = false
    setHasTimedOut(false)
  }, [])

  useEffect(() => {
    if (!isAutoJoining || hasTimedOut || attemptedRef.current || !isUserReady) return
    // The lookup is skipped for codes this short, so `preview` would never resolve.
    const isCodeTooShort = code.length < 8
    if (userId && mongoUserId && !isCodeTooShort && preview === undefined) return
    attemptedRef.current = true
    setEvent(events.lounge.join_lounge_link_opened, { userId, joinCode: code })

    const trackFailure = (reason: string) => {
      setEvent(events.lounge.join_lounge_link_failed, { userId, joinCode: code, reason })
      setIsAutoJoining(false)
    }

    if (!userId || !mongoUserId) {
      trackFailure('missing_user')
      return
    }
    if (isCodeTooShort || !preview) {
      trackFailure('invalid_code')
      return
    }
    if (preview.status !== 'active') {
      trackFailure('lounge_inactive')
      return
    }

    isJoinInFlightRef.current = true
    handleJoin({ fromLink: true }).then((failureReason) => {
      isJoinInFlightRef.current = false
      if (failureReason) trackFailure(failureReason)
    })
  }, [isAutoJoining, hasTimedOut, isUserReady, userId, mongoUserId, code, preview, handleJoin, setEvent])

  return { isAutoJoining, hasTimedOut, retry }
}

type LoungePreview = NonNullable<FunctionReturnType<typeof api.lounges.queries.getLoungeByJoinCode>>

function LoungePreviewCard({ preview }: { preview: LoungePreview | null }) {
  return (
    <View style={styles.previewCard}>
      {preview === null ? (
        <DS.Text variant="Body Large/Regular" style={{ color: DS.palette['Utility/Error'] }}>
          {translate('loungeJoinScreen.invalidCode')}
        </DS.Text>
      ) : (
        <DS.Frame gap="12">
          <DS.Frame row gap="12" style={{ alignItems: 'center' }}>
            <LoungeAvatar name={preview.name} branding={preview.branding} size={40} />
            <DS.Frame gap="4">
              <DS.Text variant="Body Large/Medium">{preview.name}</DS.Text>
              <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
                {preview.status === 'active'
                  ? translate('loungeJoinScreen.activeLounge')
                  : translate('loungeJoinScreen.loungeUnavailable')}
              </DS.Text>
              <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
                {translate('loungeJoinScreen.memberCount', {
                  current: preview.activeMemberCount,
                  max: preview.maxMembers
                })}
              </DS.Text>
            </DS.Frame>
          </DS.Frame>
          {preview.isFull && (
            <DS.Text variant="Body Small/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
              {translate('loungeJoinScreen.atCapacity')}
            </DS.Text>
          )}
        </DS.Frame>
      )}
    </View>
  )
}

function JoinHeader() {
  const router = useRouter()
  return (
    <View style={styles.header}>
      <Pressable
        onPress={() => (router.canGoBack() ? router.back() : router.push('/lounges' as any))}
        style={styles.backButton}
      >
        <Image source={backIcon} style={styles.backIcon} resizeMode="contain" />
      </Pressable>
      <DS.Text variant="Heading 3/Medium">Join a Lounge</DS.Text>
      <View style={{ width: 40 }} />
    </View>
  )
}

function AutoJoiningView({ loungeName }: { loungeName?: string }) {
  return (
    <View style={styles.autoJoining}>
      <ActivityIndicator size="large" color={DS.palette['Brand/Maroon']} />
      <DS.Text variant="Body Large/Regular" style={{ color: DS.palette['Brand/Dark Grey'], textAlign: 'center' }}>
        {loungeName
          ? translate('loungeJoinScreen.joiningLounge', { name: loungeName })
          : translate('loungeJoinScreen.joining')}
      </DS.Text>
    </View>
  )
}

function AutoJoinTimeoutView({ onRetry, onExit }: { onRetry: () => void; onExit: () => void }) {
  return (
    <View style={styles.autoJoining}>
      <DS.Text variant="Heading 3/Medium" style={{ textAlign: 'center' }}>
        {translate('loungeJoinScreen.autoJoinTimeoutTitle')}
      </DS.Text>
      <DS.Text variant="Body Large/Regular" style={{ color: DS.palette['Brand/Dark Grey'], textAlign: 'center' }}>
        {translate('loungeJoinScreen.autoJoinTimeoutMessage')}
      </DS.Text>
      <DS.Frame gap="12" style={{ alignSelf: 'stretch' }}>
        <DS.Button type="On Light" variant="Large/Primary" backgroundColor="Brand/Maroon" onPress={onRetry}>
          {translate('loungeJoinScreen.retry')}
        </DS.Button>
        <DS.Button type="On Light" variant="Large/Secondary" onPress={onExit}>
          {translate('loungeJoinScreen.exit')}
        </DS.Button>
      </DS.Frame>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    height: 54
  },
  backButton: {
    padding: 8,
    width: 40
  },
  backIcon: {
    width: 24,
    height: 24
  },
  body: {
    flex: 1,
    padding: 24
  },
  autoJoining: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    padding: 24
  },
  input: {
    height: 52,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    paddingHorizontal: 16,
    backgroundColor: DS.palette['Global/White'],
    fontSize: 18,
    letterSpacing: 2,
    fontWeight: '600'
  },
  previewCard: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey']
  }
})
